type SessionSummary = {engine: string; context: {used_tokens:number; window_tokens:number|null}|null}
const cx = (...values: unknown[]) => values.filter(Boolean).join(' ')
const one = (value: number) => String(Math.round(value * 10) / 10)

/** A token count: `912`, `1.2k`, `84.2k`, `200k`, `1.2M`. */
export function tokenText(count: number): string {
  const n = Math.max(0, Math.round(count))
  if (n < 1_000) return String(n)
  // What would round to a thousand thousands is a million.
  if (n < 999_950) return `${one(n / 1_000)}k`
  return `${one(n / 1_000_000)}M`
}

/** `used` of `total` as a whole percentage; null when there is no total. */
export function sharePercent(used: number, total: number | null | undefined): number | null {
  if (!total || total <= 0) return null
  return Math.round((Math.max(0, used) / total) * 100)
}

const CONTEXT_WARN = 80
const CONTEXT_FULL = 95

export function Context({ session }: { session: SessionSummary }) {
  const context = session.context
  if (!context) return null
  const share = sharePercent(context.used_tokens, context.window_tokens)
  const used = tokenText(context.used_tokens)
  if (share === null || !context.window_tokens) {
    return (
      <span className="code-metric" title={`${context.used_tokens.toLocaleString('en')} tokens in its last reported context`}>
        <span className="code-metric-name">Context</span> {used}
      </span>
    )
  }
  const source = session.engine === 'codex' ? 'the context window reported by Codex for this session' : 'its context'
  const title = `Last reported context: ${context.used_tokens.toLocaleString('en')} of ${context.window_tokens.toLocaleString('en')} tokens of ${source} are used`
  return (
    <span className={cx('code-metric code-context', share >= CONTEXT_FULL ? 'is-full' : share >= CONTEXT_WARN && 'is-high')} title={title}>
      <span className="code-metric-name">Context</span>
      <span
        className="progress-track"
        role="progressbar"
        aria-label="Context used"
        aria-valuemin={0}
        aria-valuemax={context.window_tokens}
        aria-valuenow={Math.min(context.used_tokens, context.window_tokens)}
        aria-valuetext={`${used} of ${tokenText(context.window_tokens)}, ${share}%`}
      >
        <span className="progress-fill" style={{ width: `${Math.min(100, share)}%` }} />
      </span>
      <span className="code-context-tokens">
        {used} / {tokenText(context.window_tokens)}
      </span>
      <span className="code-context-share">{share}%</span>
    </span>
  )
}
